import { AchievementsServiceProvider } from '../../../achievement/services/achievements-service/achievements-service';
import {
  ActionSheetController,
  AlertController,
  App,
  Events,
  IonicPage,
  Loading,
  LoadingController,
  ModalController,
  NavController,
} from 'ionic-angular';
import { AlertServiceProvider } from '../../services/alert-service/alert-service';
import { CameraService } from '../../services/camera-service/camera-service';
import { Component } from '@angular/core';
import { ConfigServiceProvider } from '../../services/config-service/config-service';
import { ENVIRONMENT } from '../../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { LevelDefinition } from '../../../achievement/model/level/level-definition.model';
import { LogoutService } from '../../../../shared/services/logout.service';
import { PlayerServiceProvider } from '../../services/player-service/player-service';
import { SellionsTakePhoto } from '../../../../shared/components/sellions-take-photo/sellions-take-photo';
import { StorageServiceProvider } from '../../services/storage-service/storage-service';
import { take } from 'rxjs/operators';
import { TextServiceProvider } from '../../services/text-service/text-service';
import { User } from '../../services/users-service/interfaces/user.interface';
import { TenantService } from '../../services/tenant-service/tenant.service';

@IonicPage()
@Component({
  selector: 'page-settings',
  templateUrl: 'settings.html',
})
export class SettingsPage {
  public error: boolean;
  public placeholderPassword: string;
  public placeholderNewPassword: string;
  public placeholderRepeatNewPassword: string;
  public password: any = {
    old: '',
    new: '',
    repeat: '',
  };
  public loading: Loading;
  public user: User = {} as User;
  public player: User = {} as User;
  public environment = ENVIRONMENT;
  public level: LevelDefinition;

  constructor(
    public actionSheetCtrl: ActionSheetController,
    public configService: ConfigServiceProvider,
    public navCtrl: NavController,
    public textService: TextServiceProvider,
    public tenantService: TenantService,
    private achievementsService: AchievementsServiceProvider,
    private alertCtrl: AlertController,
    private alertService: AlertServiceProvider,
    private app: App,
    private events: Events,
    private http: HttpClient,
    private loadingCtrl: LoadingController,
    private logoutService: LogoutService,
    private playerService: PlayerServiceProvider,
    private storageService: StorageServiceProvider,
    private cameraService: CameraService,
    private modalCtrl: ModalController,
  ) {
    this.events.subscribe('player:photoChanged', () => this.loadPlayer());
  }

  public ngOnInit() {
    this.loadPlayer();
  }

  public loadPlayer() {
    this.playerService
      .getPlayer()
      .then((response) => (this.player = { ...response }))
      .then(() => this.getLevelDefinitions());
  }

  private getLevelDefinitions() {
    Promise.all([this.achievementsService.getLevelsDef(), this.achievementsService.getLevelsHistory()]).then(([definitions, history]) => {
      const levelId = history[0] && history[0].currentLevelId;
      this.level = definitions.find((definition: { id: any }) => definition.id === levelId);
    });
  }

  public takePhoto() {
    this.takeAndChangePhoto(1);
  }

  public selectPhoto() {
    this.takeAndChangePhoto(0);
  }

  // todo: change this, so that sourceType is an enum
  private takeAndChangePhoto(sourceType: number) {
    if (this.cameraService.canTakePhoto() || sourceType === 0) {
      const modal = this.modalCtrl.create(SellionsTakePhoto, { sourceType });
      modal.onDidDismiss((data) => {
        if (data) this.changePhoto(data);
      });
      modal.present();
    } else {
      this.showCameraError();
    }
  }

  public changePhoto(image: string): void {
    this.presentLoadingModal();

    this.playerService
      .changePhoto(image)
      .then(() => {
        this.player.photo = '';
        this.loadPlayer();
        this.loading.dismiss();
      })
      .catch((error) => {
        if (!error.status) this.showUploadError();
        else if (error.status === 413) this.showFileToBigError();
        this.loading.dismiss();
      });
  }

  private presentLoadingModal() {
    this.loading = this.loadingCtrl.create({
      content: this.textService.get('settingsSendingMessage'),
    });
    this.loading.present();
  }

  public showCameraError() {
    this.alertService.showErrorAlert(this.textService.get('settingsNoCameraError'));
  }

  public photoManagement(): void {
    let actionSheet = this.actionSheetCtrl.create({ title: this.textService.get('profilePicture'), enableBackdropDismiss: true });
    if (ENVIRONMENT.cameraEnabled) {
      actionSheet.addButton({
        text: this.textService.get('takeNewProfilePicture'),
        icon: 'camera',
        handler: () => this.takePhoto(),
      });
    }
    actionSheet.addButton({
      text: this.textService.get('uploadPictureFromDevice'),
      icon: 'image',
      handler: () => this.selectPhoto(),
    });
    if (this.player.photo) {
      actionSheet.addButton({
        text: this.textService.get('removeProfilePicture'),
        icon: 'trash',
        handler: () => {
          this.playerService.deletePhoto().catch(() => this.alertService.showNoConnAlert());
        },
      });
    }
    actionSheet.present().catch((error) => console.error(error));
  }

  public logout(): void {
    this.alertCtrl
      .create({
        title: this.textService.get('logoutPopupTitle'),
        message: this.textService.get('logoutPopupText'),
        buttons: [
          { text: this.textService.get('cancel'), role: 'cancel' },
          { text: this.textService.get('yes'), handler: () => this.logoutService.logout() },
        ],
      })
      .present();
  }

  public sync() {
    this.storageService.flushCache();
    this.http.post(this.configService.getUrl('synchronize'), {}).pipe(take(1)).subscribe();
    this.app
      .getRootNav()
      .setRoot('LoadingPage')
      .catch((error) => console.error(error));
  }

  public changePassword() {
    this.presentLoadingModal();

    if (this.password.new !== this.password.repeat) {
      this.alertService.showErrorAlert(this.textService.get('settingsWrongPasswords'));
      this.loading.dismiss();
    } else {
      this.playerService
        .changePassword(this.password.old, this.password.new)
        .then(() => {
          this.loading.dismiss();
          this.alertService
            .showInfoAlert(this.textService.get('settingsPasswordChanged'), this.textService.get('settingsChangePassword'))
            .then(() => {
              this.password.old = '';
              this.password.new = '';
              this.password.repeat = '';
            });
        })
        .catch((reason) => {
          let subTitle: string;
          if (reason.error.message === 'wrong_old_password') {
            subTitle = this.textService.get('settingsActualPassError');
          } else if (reason.error.message === 'wrong_new_password_format') {
            subTitle = this.textService.get('settingsLengthPassError');
          } else {
            subTitle = undefined;
          }

          this.loading.dismiss();
          if (reason.status === 0) {
            this.alertService.showNoConnAlert(() => this.changePassword());
          } else {
            this.alertService.showErrorAlert(subTitle);
          }
        });
    }
  }

  public showLoadingError() {
    this.alertCtrl
      .create({
        title: this.textService.get('error'),
        subTitle: this.textService.get('uploadUnexpectedError'),
        buttons: [{ text: this.textService.get('ok') }],
      })
      .present();
  }

  public showTypeError() {
    this.alertCtrl
      .create({
        title: this.textService.get('error'),
        subTitle: this.textService.get('unsupportedFileFormat'),
        buttons: [{ text: this.textService.get('ok') }],
      })
      .present();
  }

  private showFileToBigError() {
    this.alertCtrl
      .create({
        title: this.textService.get('error'),
        subTitle: this.textService.get('fileToBigErrorMessage'),
        buttons: [{ text: this.textService.get('ok') }],
      })
      .present();
  }

  private showUploadError() {
    this.alertCtrl
      .create({
        title: this.textService.get('error'),
        subTitle: this.textService.get('photoUnknownError'),
        buttons: [{ text: this.textService.get('ok') }],
      })
      .present();
  }

  public goToKeycloakPanel(): void {
    window.open(ENVIRONMENT.authServerBaseUrl + 'auth/realms/' + ENVIRONMENT.authRealm + '/account/#/personal-info', '_blank').focus();
  }
}



// WEBPACK FOOTER //
// ./src/modules/core/pages/settings/settings.ts